import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Calendar, ChevronLeft, ChevronRight, Plus, Trash2, Send, Download, Printer,
  Users as UsersIcon, Factory, FlaskConical, Wrench, GraduationCap,
  PenSquare, BedDouble, MoreHorizontal, RefreshCw, Thermometer, Palmtree, Building2, Tag,
} from 'lucide-react';
import { toast } from 'sonner';

// Icon registry — falls back to Tag for custom timecodes
const ICON_MAP = {
  Factory, FlaskConical, Wrench, GraduationCap, PenSquare, BedDouble,
  MoreHorizontal, Thermometer, Palmtree, Building2, Tag, Calendar,
};

const WORK_ORDER_META = { code: 'work_order', label: 'Work Order', color: 'bg-orange-500', icon: 'Factory' };

const fmt = (d) => d.toISOString().slice(0, 10);
const mondayOf = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const dayLabel = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' });

const ShiftSchedulingPage = () => {
  const { api, user } = useAuth();
  const [tab, setTab] = useState('mine');
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const weekStartIso = fmt(weekStart);
  const weekEnd = addDays(weekStart, 6);

  const [card, setCard] = useState(null);
  const [allUsers, setAllUsers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [workOrders, setWorkOrders] = useState([]);
  const [timecodes, setTimecodes] = useState([]);  // iter31: dynamic timecode options

  // derived helpers: CATEGORIES list + CAT_BY_VAL map
  const CATEGORIES = useMemo(() => {
    const base = timecodes.filter(t => t.active).map(t => ({
      value: t.code,
      label: t.label,
      color: t.color || 'bg-slate-500',
      icon: ICON_MAP[t.icon] || Tag,
      is_payable: t.is_payable,
    }));
    // Ensure work_order is first if missing
    if (!base.find(c => c.value === 'work_order')) {
      base.unshift({ ...WORK_ORDER_META, icon: ICON_MAP[WORK_ORDER_META.icon], is_payable: true });
    }
    return base;
  }, [timecodes]);
  const CAT_BY_VAL = useMemo(() => CATEGORIES.reduce((acc, c) => { acc[c.value] = c; return acc; }, {}), [CATEGORIES]);

  // Entry dialog
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryEditing, setEntryEditing] = useState(null);
  const [entryForm, setEntryForm] = useState(blankEntry());

  const isManager = user?.role === 'Admin' || user?.role === 'ProductionSupervisor';

  const fetchCard = async (uid) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ week_start: weekStartIso });
      if (uid) params.set('user_id', uid);
      const res = await api().get(`/timecards/weekly?${params.toString()}`);
      setCard(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load timecard');
    } finally { setLoading(false); }
  };
  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const res = await api().get(`/timecards/weekly/all-users?week_start=${weekStartIso}`);
      setAllUsers(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load all-users');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === 'mine') fetchCard(selectedUserId || undefined);
    if (tab === 'all') fetchAllUsers();
    // eslint-disable-next-line
  }, [tab, weekStartIso, selectedUserId]);

  // bootstrap supporting lists
  useEffect(() => {
    (async () => {
      try {
        const [uRes, woRes, tcRes] = await Promise.all([
          api().get('/users').catch(() => ({ data: [] })),
          api().get('/work-orders').catch(() => ({ data: [] })),
          api().get('/timecode-options?active=true').catch(() => ({ data: [] })),
        ]);
        setUsers(uRes.data || []);
        setWorkOrders(woRes.data || []);
        setTimecodes(tcRes.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const woMap = useMemo(() => workOrders.reduce((acc, w) => { acc[w.id] = w; return acc; }, {}), [workOrders]);

  const shiftWeek = (delta) => setWeekStart(addDays(weekStart, delta * 7));

  const openAddEntry = (date) => {
    setEntryEditing(null);
    const today = new Date(date + 'T08:00:00');
    const out = new Date(today); out.setHours(today.getHours() + 1);
    setEntryForm({
      user_id: card?.user?.id || user?.id,
      work_order_id: '',
      category: 'work_order',
      project_label: '',
      operation_name: '',
      notes: '',
      clock_in: today.toISOString().slice(0, 16),
      clock_out: out.toISOString().slice(0, 16),
      units_completed: 0,
    });
    setEntryOpen(true);
  };

  const openEditEntry = (entry) => {
    setEntryEditing(entry);
    setEntryForm({
      user_id: card?.user?.id || user?.id,
      work_order_id: entry.work_order_id || '',
      category: entry.category || 'work_order',
      project_label: entry.project_label || '',
      operation_name: entry.operation_name || '',
      notes: entry.notes || '',
      clock_in: (entry.clock_in || '').slice(0, 16),
      clock_out: (entry.clock_out || '').slice(0, 16),
      units_completed: entry.units_completed || 0,
    });
    setEntryOpen(true);
  };

  const saveEntry = async () => {
    if (!entryForm.clock_in || !entryForm.clock_out) { toast.error('Clock-in and Clock-out are required'); return; }
    if (entryForm.category === 'work_order' && !entryForm.work_order_id) {
      toast.error('Work Order required when category is Work Order');
      return;
    }
    const payload = {
      ...entryForm,
      work_order_id: entryForm.work_order_id || null,
      project_label: entryForm.project_label || null,
      operation_name: entryForm.operation_name || null,
      notes: entryForm.notes || null,
      clock_in: new Date(entryForm.clock_in).toISOString(),
      clock_out: new Date(entryForm.clock_out).toISOString(),
      units_completed: parseInt(entryForm.units_completed) || 0,
    };
    try {
      if (entryEditing) {
        await api().put(`/time-entries/${entryEditing.id}`, payload);
        toast.success('Entry updated');
      } else {
        await api().post('/time-entries/manual', payload);
        toast.success('Entry added');
      }
      setEntryOpen(false);
      fetchCard(selectedUserId || undefined);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await api().delete(`/time-entries/${id}`);
      fetchCard(selectedUserId || undefined);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const submitWeek = async () => {
    if (!window.confirm('Submit this week\'s entries to the timekeeper?')) return;
    try {
      const payload = { week_start: weekStartIso };
      if (selectedUserId) payload.user_id = selectedUserId;
      const res = await api().post('/timecards/weekly/submit', payload);
      toast.success(`${res.data.submitted_count} entries submitted`);
      fetchCard(selectedUserId || undefined);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const exportCsv = async (uid) => {
    try {
      const params = new URLSearchParams({ week_start: weekStartIso });
      if (uid) params.set('user_id', uid);
      const res = await api().get(`/timecards/weekly/export.csv?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `timecard_${weekStartIso}_to_${fmt(weekEnd)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
  };

  // iter38 — QuickBooks payroll-compatible export (daily aggregated)
  const exportQuickBooks = async (uid) => {
    try {
      const params = new URLSearchParams({ week_start: weekStartIso });
      if (uid) params.set('user_id', uid);
      const res = await api().get(`/timecards/quickbooks-export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `quickbooks_timecard_${weekStartIso}_to_${fmt(weekEnd)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('QuickBooks CSV downloaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'QuickBooks export failed'); }
  };

  // iter69 — branded printable timecard for the active week
  const printTimecard = async () => {
    const { openBrandedPrintWindow, PrintHelpers: H } = await import('../utils/brandedPrint');
    if (!card) { toast.error('Load a timecard week first'); return; }
    const userLabel = card?.user?.full_name || card?.user?.email || 'Me';
    const cols = [
      { key: 'date', label: 'Date', mono: true, fmt: (_, r) => r.date || '—' },
      { key: 'day', label: 'Day', fmt: (_, r) => r.date ? new Date(r.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short' }) : '—' },
      { key: 'work_order', label: 'WO Hours', mono: true, fmt: (_, r) => (r.by_category?.work_order || 0).toFixed(2) },
      { key: 'rnd', label: 'R&D', mono: true, fmt: (_, r) => (r.by_category?.rnd || 0).toFixed(2) },
      { key: 'meeting', label: 'Meeting', mono: true, fmt: (_, r) => (r.by_category?.meeting || 0).toFixed(2) },
      { key: 'pto', label: 'PTO', mono: true, fmt: (_, r) => (r.by_category?.pto || 0).toFixed(2) },
      { key: 'other', label: 'Other', mono: true, fmt: (_, r) => {
        const known = ['work_order', 'rnd', 'meeting', 'pto'];
        const o = Object.entries(r.by_category || {}).filter(([k]) => !known.includes(k)).reduce((s, [, v]) => s + (v || 0), 0);
        return o.toFixed(2);
      } },
      { key: 'total', label: 'Total Hrs', mono: true, fmt: (_, r) => (r.hours || 0).toFixed(2) },
    ];
    const meta = H.kvGrid([
      ['Employee', userLabel],
      ['Week', `${weekStartIso} → ${fmt(weekEnd)}`],
      ['Total Hours', `${(card.totals?.hours || 0).toFixed(2)} h`],
      ['Payable', `${(card.totals?.payable_hours || card.totals?.hours || 0).toFixed(2)} h`],
      ['Submitted At', card.submitted_at ? new Date(card.submitted_at).toLocaleString() : '— (not submitted)'],
      ['Status', card.status || 'open'],
    ]);
    await openBrandedPrintWindow({
      title: 'WEEKLY TIMECARD',
      subtitle: `${userLabel} · ${weekStartIso} → ${fmt(weekEnd)}`,
      orientation: 'landscape',
      statusBadge: H.statusBadge(card.status || 'open', card.status === 'submitted' ? '#16a34a' : card.status === 'approved' ? '#0ea5e9' : '#6b7280'),
      body: `${meta}${H.sectionTitle('Daily Breakdown')}${H.table(cols, days)}${H.totalBox('Total Payable Hours', `${(card.totals?.payable_hours || card.totals?.hours || 0).toFixed(2)} h`)}${H.signoffLines('Employee Signature', 'Supervisor Signature')}`,
      footerNote: 'Internal Document · Timecard',
    });
  };

  const totalWeekHours = card?.totals?.hours || 0;
  const days = card?.days || [];

  return (
    <div className="space-y-4 animate-slideIn" data-testid="shift-scheduling-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Timecard</h1>
          <p className="text-slate-500 text-sm">Weekly hours by category. Pulled from production timers + R&amp;D, meetings, PTO and other rows you add manually.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => shiftWeek(-1)} data-testid="week-prev-btn"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="text-sm font-mono px-3 py-1.5 border border-slate-200 rounded-sm" data-testid="week-window-label">
            {weekStartIso} → {fmt(weekEnd)}
          </div>
          <Button size="sm" variant="outline" onClick={() => shiftWeek(1)} data-testid="week-next-btn"><ChevronRight className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="week-this-btn">This Week</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="mine" data-testid="tab-my-timecard"><Calendar className="w-4 h-4 mr-2" /> My Timecard</TabsTrigger>
          {isManager && <TabsTrigger value="all" data-testid="tab-all-timecards"><UsersIcon className="w-4 h-4 mr-2" /> All Users</TabsTrigger>}
        </TabsList>

        {/* MY TIMECARD */}
        <TabsContent value="mine" className="mt-4 space-y-4">
          {isManager && (
            <div className="flex items-center gap-2">
              <Label className="text-xs uppercase text-slate-500">View user</Label>
              <Select value={selectedUserId || 'self'} onValueChange={(v) => setSelectedUserId(v === 'self' ? '' : v)}>
                <SelectTrigger className="w-72 h-9" data-testid="user-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">— My timecard —</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="ghost" onClick={() => fetchCard(selectedUserId || undefined)} title="Refresh"><RefreshCw className="w-4 h-4" /></Button>
            </div>
          )}

          {loading || !card ? (
            <div className="h-48 flex items-center justify-center"><div className="spinner" /></div>
          ) : (
            <>
              {/* Totals row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="timecard-totals-row">
                <Stat label="Total Hours" value={totalWeekHours} mono testid="total-hours-stat" />
                <Stat label="Units Built" value={card.totals.units_completed} testid="units-built-stat" />
                <Stat label="Finished Units" value={card.totals.finished_units_built} testid="fu-built-stat" />
                <Stat label="QC (P / F)" value={`${card.totals.qc_pass} / ${card.totals.qc_fail}`} testid="qc-stat" />
              </div>

              {/* Category bar */}
              {Object.keys(card.totals.by_category || {}).length > 0 && (
                <Card className="industrial-card">
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Hours by Category</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(card.totals.by_category).map(([cat, mins]) => (
                        <Badge key={cat} className={`${CAT_BY_VAL[cat]?.color || 'bg-slate-500'} text-white`} data-testid={`cat-pill-${cat}`}>
                          {CAT_BY_VAL[cat]?.label || cat}: {(mins / 60).toFixed(2)} h
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Weekly grid */}
              <Card className="industrial-card">
                <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Daily Detail</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => printTimecard()} data-testid="timecard-print-btn"><Printer className="w-4 h-4 mr-1" /> Print</Button>
                    <Button size="sm" variant="outline" onClick={() => exportCsv(card.user.id)} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
                    <Button size="sm" variant="outline" onClick={() => exportQuickBooks(card.user.id)} data-testid="export-quickbooks-btn" title="QuickBooks-compatible payroll CSV"><Download className="w-4 h-4 mr-1" /> QuickBooks</Button>
                    <Button size="sm" onClick={submitWeek} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-week-btn"><Send className="w-4 h-4 mr-1" /> Submit Week</Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="grid grid-cols-7 divide-x divide-slate-100 border-b border-slate-100">
                    {days.map(d => (
                      <div key={d.date} className="p-2 text-center bg-slate-50">
                        <p className="text-xs font-bold text-slate-700">{dayLabel(d.date)}</p>
                        <p className="text-lg font-mono mt-0.5" data-testid={`day-hours-${d.date}`}>{(d.minutes_total / 60).toFixed(2)}h</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 divide-x divide-slate-100 min-h-[180px]">
                    {days.map(d => (
                      <div key={d.date} className="p-1.5 space-y-1 bg-white" data-testid={`day-cell-${d.date}`}>
                        {d.entries.map(e => {
                          const cat = CAT_BY_VAL[e.category] || CAT_BY_VAL.work_order || { color: 'bg-slate-500', icon: Tag, label: e.category || 'Unknown' };
                          const Icon = cat.icon || Tag;
                          return (
                            <button key={e.id} onClick={() => openEditEntry(e)} className="w-full text-left text-xs border border-slate-200 hover:border-orange-400 rounded-sm px-1.5 py-1 group" data-testid={`entry-card-${e.id}`}>
                              <div className="flex items-center gap-1">
                                <span className={`w-2 h-2 rounded-full ${cat.color}`} />
                                <span className="font-mono text-[10px] text-slate-500">{(e.duration_minutes / 60).toFixed(2)}h</span>
                                {e.is_active && <Badge className="bg-yellow-500 text-[9px] py-0 px-1 ml-auto">LIVE</Badge>}
                                {e.approval_status === 'submitted' && <Badge className="bg-slate-500 text-[9px] py-0 px-1 ml-auto">submitted</Badge>}
                                {e.approval_status === 'approved' && <Badge className="bg-emerald-600 text-[9px] py-0 px-1 ml-auto">approved</Badge>}
                              </div>
                              <p className="font-medium truncate flex items-center gap-1"><Icon className="w-3 h-3 inline" />{e.work_order_label || e.project_label || cat.label}</p>
                              {e.notes && <p className="text-[10px] text-slate-500 truncate">{e.notes}</p>}
                            </button>
                          );
                        })}
                        <Button size="sm" variant="ghost" className="w-full h-7 text-[11px] text-slate-400 hover:text-slate-700" onClick={() => openAddEntry(d.date)} data-testid={`add-entry-${d.date}`}>
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* By WO */}
              {(card.totals.by_work_order || []).length > 0 && (
                <Card className="industrial-card">
                  <CardHeader className="border-b border-slate-100 pb-3"><CardTitle className="text-base">Top Work Orders this Week</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <Table className="data-table">
                      <TableHeader><TableRow><TableHead>Work Order</TableHead><TableHead className="text-right">Hours</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {card.totals.by_work_order.map(w => (
                          <TableRow key={w.work_order_id}>
                            <TableCell className="text-sm">{w.label}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{(w.minutes / 60).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* ALL USERS */}
        {isManager && (
          <TabsContent value="all" className="mt-4 space-y-3">
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => exportCsv(null)} data-testid="export-all-csv-btn"><Download className="w-4 h-4 mr-1" /> Export All Users CSV</Button>
              <Button size="sm" variant="outline" onClick={() => exportQuickBooks(null)} data-testid="export-all-quickbooks-btn" title="QuickBooks-compatible payroll CSV (all users)"><Download className="w-4 h-4 mr-1" /> QuickBooks (All Users)</Button>
            </div>
            <Card className="industrial-card">
              <CardContent className="p-0">
                {loading || !allUsers ? (
                  <div className="h-32 flex items-center justify-center"><div className="spinner" /></div>
                ) : (
                  <Table className="data-table">
                    <TableHeader><TableRow>
                      <TableHead>Employee</TableHead><TableHead>Role</TableHead><TableHead className="text-right">Hours</TableHead><TableHead>Categories</TableHead><TableHead className="text-right">Units</TableHead><TableHead>Entries</TableHead><TableHead className="text-right">Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {allUsers.users.map(u => (
                        <TableRow key={u.user_id} data-testid={`all-user-row-${u.user_id}`}>
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell className="text-xs text-slate-500">{u.role}</TableCell>
                          <TableCell className="text-right font-mono">{u.total_hours}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(u.by_category).map(([c, m]) => (
                                <Badge key={c} variant="outline" className="text-[10px]">{CAT_BY_VAL[c]?.label || c}: {(m / 60).toFixed(1)}h</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{u.units_completed}</TableCell>
                          <TableCell>
                            <Badge className={u.all_approved ? 'bg-emerald-600' : 'bg-slate-500'}>{u.entries_count} {u.all_approved ? 'approved' : ''}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => { setSelectedUserId(u.user_id); setTab('mine'); }} data-testid={`open-user-${u.user_id}`}>Open</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* ENTRY DIALOG */}
      <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{entryEditing ? 'Edit Time Entry' : 'Add Time Entry'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Category</Label>
              <Select value={entryForm.category} onValueChange={v => setEntryForm({ ...entryForm, category: v, work_order_id: v === 'work_order' ? entryForm.work_order_id : '' })}>
                <SelectTrigger data-testid="entry-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {entryForm.category === 'work_order' && (
              <div className="space-y-1">
                <Label>Work Order *</Label>
                <Select value={entryForm.work_order_id || 'none'} onValueChange={v => setEntryForm({ ...entryForm, work_order_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="entry-wo-select"><SelectValue placeholder="Select WO" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>— select —</SelectItem>
                    {workOrders.slice(0, 80).map(w => <SelectItem key={w.id} value={w.id}>WO-{w.id.slice(0, 8).toUpperCase()} · {w.queue_type?.replace('_', ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {entryForm.category !== 'work_order' && (
              <div className="space-y-1">
                <Label>Project / Task</Label>
                <Input value={entryForm.project_label} onChange={e => setEntryForm({ ...entryForm, project_label: e.target.value })} placeholder="e.g., Solar panel R&D" data-testid="entry-project-input" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Clock In *</Label><Input type="datetime-local" value={entryForm.clock_in} onChange={e => setEntryForm({ ...entryForm, clock_in: e.target.value })} data-testid="entry-clock-in-input" /></div>
              <div className="space-y-1"><Label>Clock Out *</Label><Input type="datetime-local" value={entryForm.clock_out} onChange={e => setEntryForm({ ...entryForm, clock_out: e.target.value })} data-testid="entry-clock-out-input" /></div>
            </div>
            <div className="space-y-1"><Label>Operation Name</Label><Input value={entryForm.operation_name} onChange={e => setEntryForm({ ...entryForm, operation_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Units Completed</Label><Input type="number" min="0" value={entryForm.units_completed} onChange={e => setEntryForm({ ...entryForm, units_completed: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Notes</Label><Textarea rows={2} value={entryForm.notes} onChange={e => setEntryForm({ ...entryForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setEntryOpen(false)}>Cancel</Button>
            {entryEditing && entryEditing.approval_status !== 'approved' && (
              <Button variant="outline" className="text-red-600" onClick={() => { deleteEntry(entryEditing.id); setEntryOpen(false); }} data-testid="entry-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
            )}
            <Button onClick={saveEntry} className="bg-orange-500 hover:bg-orange-600" data-testid="entry-save-btn">{entryEditing ? 'Save Changes' : 'Add Entry'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const blankEntry = () => ({
  user_id: '', work_order_id: '', category: 'work_order', project_label: '',
  operation_name: '', notes: '', clock_in: '', clock_out: '', units_completed: 0,
});

const Stat = ({ label, value, mono, testid }) => (
  <Card className="industrial-card">
    <CardContent className="p-3">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold ${mono ? 'font-mono' : ''}`} data-testid={testid}>{value}</p>
    </CardContent>
  </Card>
);

export default ShiftSchedulingPage;
